import { getMinAndMax } from '@intelligence/v3/utils/number';

export interface IRankAxisOverrides {
  min: number;
  max?: number;
  interval?: number;
}

/**
 * 计算排名类 Y 轴的 min/max/interval 覆盖值，
 * 用于解决排名趋势线沉底、排名稳定时刻度不合理等问题。
 *
 * @param rankValues 当前可见的所有排名数值（已过滤 NaN / Infinity）
 * @param yAxisMin   Y 轴起始值，排名场景一般为 1
 */
export function computeRankAxisOverrides(
  rankValues: number[],
  yAxisMin: number = 1,
): IRankAxisOverrides {
  if (rankValues.length === 0) {
    return { min: 0, max: 1, interval: 1 };
  }

  const { min: dataMin, max: dataMax } = getMinAndMax(rankValues);

  // Top1 稳定：所有数据 ≤ 1，固定刻度 1,2,3 使趋势线置顶
  if (dataMax <= 1) {
    return { min: yAxisMin, max: 3, interval: 1 };
  }

  // 非第一稳定：所有数据值相同，以数据值为中心等距扩展使趋势线居中
  if (dataMax === dataMin) {
    const gap = dataMax - yAxisMin;
    return {
      min: yAxisMin,
      max: dataMax + (gap > 0 ? gap : 2),
      interval: gap > 0 ? gap : 1,
    };
  }

  // 数据聚集在远离 yAxisMin 的区域：以数据中点为基准扩展 max 使趋势线居中
  const dataRange = dataMax - dataMin;
  const gapFromAxisMin = dataMin - yAxisMin;
  if (gapFromAxisMin > 0 && gapFromAxisMin > dataRange) {
    const midpoint = (dataMax + dataMin) / 2;
    const extendedMax = yAxisMin + 2 * (midpoint - yAxisMin);
    const halfRange = (extendedMax - yAxisMin) / 2;
    return {
      min: yAxisMin,
      max: extendedMax,
      interval: halfRange < 0.5 ? halfRange : Math.round(halfRange),
    };
  }

  return { min: yAxisMin };
}
